import React, { useState } from 'react';
import { Form, Button, Row, Col, Container, Alert } from 'react-bootstrap';
import axios from 'axios';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useNavigate } from "react-router-dom";

import '../../styles/CustomerForm.css';

const CustomerForm = ({ initialData = {} }) => {
  const [formData, setFormData] = useState({
    customer_id: initialData.customer_id || '',
    company_name: initialData.company_name || '',
    email: initialData.email || '',
    phone: initialData.phone || '',
    address: initialData.address || '',
    city: initialData.city || '',
    province: initialData.province || 'Saskatchewan',
    username: initialData.username || '',
    password: initialData.password || '',
    postal_code: initialData.postal_code || ''
  });
  const navigate = useNavigate();

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false); // 👈 Toggle visibility
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const provinces = [
    "Alberta", "British Columbia", "Manitoba", "New Brunswick",
    "Newfoundland and Labrador", "Nova Scotia", "Ontario",
    "Prince Edward Island", "Quebec", "Saskatchewan",
    "Northwest Territories", "Nunavut", "Yukon"
  ];

  const isPasswordStrong = (password) => {
    return (
      password.length >= 8 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[^A-Za-z0-9]/.test(password)
    );
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleAddCustomer = async (formData) => {
    setError("");
    setSuccess("");

    if (!isPasswordStrong(formData.password)) {
      setError("Password must be at least 8 characters and include uppercase, lowercase, number, and special character.");
      return;
    }

    try {
      const token = localStorage.getItem("admin_token");

      await axios.post(`${API_BASE}/api/customers_a/add`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess("Customer added successfully ! Redirecting ...");
      setFormData({ ...formData, password: '' }); // Clear password
      setTimeout(() => navigate("/customer/ManageOrder"), 1000);

    
    } catch (err) {
      if (err.response?.status === 409) {
        const message = err.response.data?.error;
        if (message === "username_exists") {
          setError("Username already exists.");
        } else if (message === "email_exists") {
          setError("Email already exists.");
        } else {
          setError("Username or email already exists.");
        }
      } else {
        setError("Something went wrong. Please try again.");
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleAddCustomer(formData);
  };

  return (
    <Container>
      <div className="order-form-container">
        <h2 className="order-form">
          {initialData.customer_id ? 'Edit' : 'Add'} Customer
        </h2>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Form className="order-form" onSubmit={handleSubmit}>
          <Form.Group as={Row} controlId="company_name">
            <Form.Label column sm={4}>Company Name</Form.Label>
            <Col sm={8}>
              <Form.Control
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                required
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row} controlId="username">
            <Form.Label column sm={4}>Username</Form.Label>
            <Col sm={8}>
              <Form.Control
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row} controlId="password">
            <Form.Label column sm={4}>Password</Form.Label>
            <Col sm={8} className="position-relative">
              <Form.Control
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                isInvalid={formData.password && !isPasswordStrong(formData.password)}
              />
              <span
                className="position-absolute end-0 top-50 translate-middle-y pe-3"
                style={{ cursor: "pointer", zIndex: 10 }}
                onClick={() => setShowPassword(prev => !prev)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
              {formData.password && !isPasswordStrong(formData.password) && (
                <Form.Text className="text-danger d-block">
                  Password must be at least 8 characters and include uppercase, lowercase, number, and special character.
                </Form.Text>
              )}
            </Col>
          </Form.Group>

          <Form.Group as={Row} controlId="email">
            <Form.Label column sm={4}>Email</Form.Label>
            <Col sm={8}>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row} controlId="phone">
            <Form.Label column sm={4}>Phone</Form.Label>
            <Col sm={8}>
              <Form.Control
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row} controlId="address">
            <Form.Label column sm={4}>Address</Form.Label>
            <Col sm={8}>
              <Form.Control
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row} controlId="city">
            <Form.Label column sm={4}>City</Form.Label>
            <Col sm={8}>
              <Form.Control
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row} controlId="postal_code">
            <Form.Label column sm={4}>Postal Code</Form.Label>
            <Col sm={8}>
              <Form.Control
                type="text"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
              />
            </Col>
          </Form.Group>

          <Form.Group as={Row} controlId="province">
            <Form.Label column sm={4}>Province</Form.Label>
            <Col sm={8}>
              <Form.Select
                name="province"
                value={formData.province}
                onChange={handleChange}
                required
              >
                <option value="">-- Select Province --</option>
                {provinces.map((prov) => (
                  <option key={prov} value={prov}>{prov}</option>
                ))}
              </Form.Select>
            </Col>
          </Form.Group>

          <Row>
            <Col sm={{ span: 8, offset: 4 }}>
              <Button type="submit">
                {initialData.customer_id ? 'Update' : 'Add'} Customer
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    </Container>
  );
};

export default CustomerForm;
